'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface BlogPost {
  id: number;
  titleEs: string;
  titleFr: string;
  image: string;
  alt: string;
  href: string;
  dateEs: string;
  dateFr: string;
  categoryEs: string;
  categoryFr: string;
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    titleEs: 'Plataformas de Educación Online registran más de 342 000 usuarios activos en Chile',
    titleFr: 'Les plateformes d\'éducation en ligne enregistrent plus de 342 000 utilisateurs actifs au Chili',
    image: 'https://innobox.cl/wp-content/uploads/2025/04/e-learning-637x446.png',
    alt: 'E-learning - plataformas de educación online en Chile con estudiantes usando computadores',
    href: '/blog/plataformas-educacion-online',
    dateEs: 'Abril 22, 2025',
    dateFr: '22 Avril 2025',
    categoryEs: 'Tecnología',
    categoryFr: 'Technologie',
  },
  {
    id: 2,
    titleEs: 'Smartwatches y Salud Conectada: Revolucionando la Monitorización de Pacientes en Chile',
    titleFr: 'Smartwatches et Santé Connectée: Révolutionner la Surveillance des Patients au Chili',
    image: 'https://innobox.cl/wp-content/uploads/2025/04/Smartwatches--637x446.png',
    alt: 'Smartwatches y salud conectada - dispositivos wearables para monitorización de pacientes',
    href: '/blog/smartwatches-salud-conectada',
    dateEs: 'Abril 22, 2025',
    dateFr: '22 Avril 2025',
    categoryEs: 'Innovación',
    categoryFr: 'Innovation',
  },
  {
    id: 3,
    titleEs: 'Ciberseguridad en Chile: Aumento del 30% en Cibercrimen Organizado durante 2024',
    titleFr: 'Cybersécurité au Chili: Augmentation de 30% de la Cybercriminalité Organisée en 2024',
    image: 'https://innobox.cl/wp-content/uploads/2025/04/ciberseguridad-637x446.png',
    alt: 'Ciberseguridad en Chile - escudo digital protegiendo datos empresariales',
    href: '/blog/ciberseguridad-chile-2024',
    dateEs: 'Abril 22, 2025',
    dateFr: '22 Avril 2025',
    categoryEs: 'Ciberseguridad',
    categoryFr: 'Cybersécurité',
  },
];

const BlogSection: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionTitle = language === 'fr' ? 'Actualités' : 'Noticias';
  const sectionSubtitle = language === 'fr' ? 'Insights & Tendances' : 'Insights & Tendencias';
  const readMore = language === 'fr' ? 'Voir toutes les actualités' : 'Ver todas las noticias';
  const readPost = language === 'fr' ? 'Lire l\'article' : 'Leer artículo';

  return (
    <section
      id="noticias"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#141618', paddingTop: '120px', paddingBottom: '120px' }}>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        <div className="absolute left-0 right-0 top-0 h-px bg-white/6" />
        <div className="absolute left-0 right-0 bottom-0 h-px bg-white/6" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className={`flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-px bg-[#abd233]" />
              <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                {sectionTitle}
              </span>
            </div>
            <h2
              className="text-white"
              style={{
                fontSize: 'clamp(28px, 3vw, 48px)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
              }}>
              {sectionSubtitle}
            </h2>
          </div>
          <a
            href="https://innobox.cl/blog/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center overflow-hidden flex-shrink-0"
            style={{
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '11px 22px',
              fontSize: '13px',
              color: '#ffffff',
              letterSpacing: '0.02em',
              transition: 'all 300ms ease-out',
            }}>
            <span
              className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
              style={{ zIndex: 0 }}
            />
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
              {readMore}
            </span>
          </a>
        </div>

        {/* Blog grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {blogPosts.map((post, index) => {
            const title = language === 'fr' ? post.titleFr : post.titleEs;
            const date = language === 'fr' ? post.dateFr : post.dateEs;
            const category = language === 'fr' ? post.categoryFr : post.categoryEs;
            return (
              <article
                key={post.id}
                className="group border border-white/6 hover:border-[#abd233]/30 transition-all duration-300"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${index * 120}ms, transform 0.6s ease ${index * 120}ms, border-color 0.3s`,
                }}>
                {/* Image */}
                <div className="overflow-hidden" style={{ aspectRatio: '16/10' }}>
                  <a href={post.href}>
                    <img
                      src={post.image}
                      alt={post.alt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </a>
                </div>

                {/* Content */}
                <div className="p-6" style={{ backgroundColor: '#1a1d20' }}>
                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="text-[#abd233]"
                      style={{
                        fontSize: '11px',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        border: '1px solid rgba(171,210,51,0.3)',
                        padding: '2px 8px',
                      }}>
                      {category}
                    </span>
                    <time className="text-white/30" style={{ fontSize: '12px' }}>
                      {date}
                    </time>
                  </div>

                  <h3
                    className="text-white mb-4 group-hover:text-[#abd233] transition-colors duration-300"
                    style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.4, letterSpacing: '-0.01em' }}>
                    <a href={post.href}>{title}</a>
                  </h3>

                  <a
                    href={post.href}
                    className="inline-flex items-center gap-2 text-white/40 hover:text-[#abd233] transition-colors duration-300"
                    style={{ fontSize: '12px', letterSpacing: '0.05em' }}>
                    {readPost}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1 5h8M5.5 1.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;